import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RouteComponentProps } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";
import {
  type BookedFlightItineraryWithDepartureTime,
  FlightAction,
  MultiTicketType,
  Status,
  type AssociatedPassenger,
} from "@b2bportal/air-booking-api";
import { ChatPropertiesType } from "@b2bportal/chat-api";
import {
  DisruptionAlertBannerType,
  disruptionExerciseFlags,
  DisruptionExerciseProgress,
  getDisruptionExerciseProgress,
  isMissedConnectionContract,
  missedConnectionExerciseFlags,
  setDisruptionExerciseProgress,
} from "@b2bportal/core-disruption";
import { useRebookFlightTripAction } from "@b2bportal/core-flights-rebook";
import { trackEvent } from "@hopper-b2b/api";

import { useI18nContext } from "@hopper-b2b/i18n";
import {
  UberExchangeFlightModalContent,
  useChatSupport,
} from "@hopper-b2b/self-serve";
import {
  ChfarExerciseProgress,
  ClientName,
  FlightItineraryState,
  type FlightSummaryInfo,
  type FlightSummaryInfoWithKeys,
  getFlightInfoDetails,
  getFlightSummaryInfo,
  getReturnSlice,
  type IPerson,
  MyTripsFilter,
  MyTripsModalTypes,
  ScheduleChangeSeverity,
  TagColors,
  type TagInfo,
  TRIPS_FILTER_QUERY_PARAM,
  VIEWED_TRIP_CARD,
} from "@hopper-b2b/types";
import {
  ActionLinkKey,
  AirlineIcon,
  ButtonWrap,
  type IActionLink,
  IconComponent,
  IconName,
  MobileFlightSummaryRowNew,
  PassengerSummaryRow,
  Slot,
  StatusTag,
  UberPriceSummaryRow,
} from "@hopper-b2b/ui";
import {
  getEnvVariables,
  useEnableAirExchange,
  useEnableCfarExerciseCore,
  useEnableDisruptionRebookingContactSupport,
  useUberBridge,
} from "@hopper-b2b/utilities";
import { Divider, Popover, Typography } from "@material-ui/core";
import { FlightDisruptionAlertBanner } from "@tenant/disruption";
import clsx from "clsx";
import dayjs from "dayjs";

import { ClientContext } from "../../../../../../ClientContext";
import {
  allowsInAppFlightAction,
  resolveFlightActionRoute,
} from "../../../../../../utils/flightActionCapabilities";
import {
  PATH_DISRUPTION_EXERCISE,
  PATH_HOME,
} from "../../../../../../utils/paths";
import {
  cfarFlightsExerciseRelativeRoute,
  flightsItineraryRelativeRoute,
} from "../../../../../../utils/routes";
import {
  fetchFlightCfarCancellationQuote,
  populateTripQueryParams,
  setChfarExerciseProgress,
} from "../../../../../TripsList/actions/actions";
import { addFlightType } from "../../../../../TripsList/components/ItineraryList/components/FlightCard/helpers";
import { getConfirmationNumbers } from "../../../../../TripsList/components/ItineraryList/components/ItinerariesModal/components/ConfirmationModalContent/component";
import { MobileActionLinks } from "../../../../../TripsList/components/ItineraryList/components/MobileActionLinks";
import { StatusBanner } from "../../../../../TripsList/components/ItineraryList/components/StatusBanner";
import { getFlightTotalWithSeats } from "../../../../../TripsList/utils/helpers";
import { DATE_FORMAT } from "../../constants";
import type { MobileFlightItineraryDetailsConnectorProps } from "./container";
import "./styles.scss";

enum ISelfServePage {
  cancel = "cancel",
  exchange = "exchange",
}

interface IMobileFlightItineraryDetailsProps
  extends RouteComponentProps,
    MobileFlightItineraryDetailsConnectorProps {
  onSupportClick?: (
    productId?: string,
    productType?: ChatPropertiesType,
    requestType?: string
  ) => void;
  onGoToMyTrips?: () => void;
}

export const MobileFlightItineraryDetails = ({
  flight,
  airportMap,
  airlineMap,
  fetchFlights,
  setSelectedFlight,
  history,
  setOpenModal,
  tripsFilter,
  onSupportClick,
  onGoToMyTrips,
}: IMobileFlightItineraryDetailsProps) => {
  const disruptionExerciseProgress = useSelector(getDisruptionExerciseProgress);
  const { generalSupportChat } = useChatSupport();
  const clientContext = useContext(ClientContext);
  const { t, formatFiatCurrency, brand } = useI18nContext();
  const { setHeader } = useUberBridge();
  const {
    status,
    travelCredit,
    travelCreditCopy,
    ancillaries,
    cfar: cfarContract,
    bookedItinerary: { id: itineraryId },
  } = flight ?? {};
  const ftcPopoverRef = useRef<HTMLButtonElement>(null);
  const [ftcPopoverOpen, setFTCPopoverOpen] = useState(false);
  const isCancelled = status === Status.Canceled;
  const isPastTrips = tripsFilter === MyTripsFilter.PAST_TRIPS;

  const navigate = useNavigate();
  const isCfarExerciseCoreEnabled = useEnableCfarExerciseCore();
  const hasCfarCore = isCfarExerciseCoreEnabled && cfarContract?.isExercisable;

  const tripHasAirChar = ancillaries?.cfar?.isExercisable;
  const tripHasAirChfar = ancillaries?.chfar?.isExercisable;

  const showAirCfar = tripHasAirChar;
  const showAirChfar = tripHasAirChfar;
  const dispatch = useDispatch();
  const enableAirExchange = useEnableAirExchange();
  const isNubank = getEnvVariables("clientName") === ClientName.NUBANK;
  const [selfServePage, setSelfServePage] = useState<ISelfServePage | null>(
    null
  );
  const hasFTC = !!travelCredit && !!travelCreditCopy;
  const { disruptionProtection } = flight.ancillaries;

  const missedConnection = isMissedConnectionContract(disruptionProtection)
    ? disruptionProtection
    : flight.ancillaries.missedConnection;

  const hasDisruptionProtection = !!disruptionProtection;
  const { isDisruptionProtectionExercisable, isEligibleToBookReturn } =
    disruptionExerciseFlags(disruptionProtection);
  const isMissedConnectionProtectionExercisable =
    missedConnectionExerciseFlags(missedConnection);

  // When the BFF sends `actionCapabilities` it is the ONLY routing
  // source for cancel / CFAR / DAFAR, never contract presence, user cohort,
  // or booking origin. When it sends none (this screen is shared with uber)
  // every route resolves to `not-computed`, `allowsInAppFlightAction` is
  // always true, and each branch below behaves exactly as it did before.
  //
  // A commerce hand-off also needs the host callback: a tenant that gets
  // capabilities but wires no handler hides the CTA rather than dead-ending
  // the user (defensive; only nubank receives capabilities today).
  const onCommerceFlightAction = clientContext?.onCommerceFlightAction;
  const {
    cancel: cancelRoute,
    cfar: cfarRoute,
    dafar: dafarRoute,
  } = useMemo(
    () => ({
      cancel: resolveFlightActionRoute(flight, FlightAction.Cancel),
      cfar: resolveFlightActionRoute(flight, FlightAction.Cfar),
      dafar: resolveFlightActionRoute(flight, FlightAction.Dafar),
    }),
    [flight]
  );

  const goBack = useCallback(
    (didCancel?: boolean) => {
      if (selfServePage) {
        setHeader({
          title: t("tripSummary"),
        });
        setSelfServePage(null);
        if (didCancel) {
          // we want to keep this call in sync with the fetchFlights call in UberTripsList/component.tsx
          // please cross reference any changes with that function
          fetchFlights(
            {
              states: [
                FlightItineraryState.Canceled,
                FlightItineraryState.Future,
                FlightItineraryState.Present,
                FlightItineraryState.Past,
              ],
              referenceDateTime: dayjs().format(),
            },
            history
          );
        }
      } else if (onGoToMyTrips) {
        setSelectedFlight(null);
        onGoToMyTrips();
      } else {
        setSelectedFlight(null);
        history.push(`${PATH_HOME}?${TRIPS_FILTER_QUERY_PARAM}=${tripsFilter}`);
      }
    },
    [
      fetchFlights,
      history,
      onGoToMyTrips,
      selfServePage,
      setHeader,
      setSelectedFlight,
      t,
      tripsFilter,
    ]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent({ eventName: VIEWED_TRIP_CARD, properties: undefined });
  }, []);

  useEffect(() => {
    setHeader({
      title: t("tripSummary"),
    });
  }, [setHeader, t]);

  const confirmationNumbers = useMemo(() => {
    if (flight) {
      const allConfirmationNumbers = getConfirmationNumbers({
        flight,
        airlineMap,
      });
      return allConfirmationNumbers.filter(
        (confirmationNumber, index, arr) =>
          index ===
          arr.findIndex((item) => item.locator === confirmationNumber.locator)
      );
    }
  }, [flight, airlineMap]);

  const isVirtualInterlining = useMemo(
    () =>
      flight.bookedItinerary.multiTicketType ===
      MultiTicketType.virtualInterline,
    [flight]
  );

  const isMultiTypeTicket = useMemo(
    () =>
      flight.bookedItinerary.multiTicketType === MultiTicketType.hackerFare ||
      flight.bookedItinerary.multiTicketType ===
        MultiTicketType.virtualInterline,
    [flight]
  );

  const closeFTCPopover = () => {
    setFTCPopoverOpen(false);
  };

  const contactSupport = useCallback(() => {
    if (onSupportClick) {
      onSupportClick(
        flight.bookedItinerary.id,
        ChatPropertiesType.Air,
        "General"
      );
    } else {
      generalSupportChat();
    }
  }, [onSupportClick, generalSupportChat, flight]);

  const onOpenModal = useCallback(
    (type: MyTripsModalTypes) => {
      switch (type) {
        case MyTripsModalTypes.ContactSupport:
          contactSupport();
          break;
        case MyTripsModalTypes.ScheduleChange:
          generalSupportChat();
          break;
        case MyTripsModalTypes.ExchangeFlight:
          setSelfServePage(ISelfServePage.exchange);
          break;
        default:
          setOpenModal({
            type,
            selectedItinerary: addFlightType(flight),
          });
          break;
      }
    },
    [flight, generalSupportChat, setOpenModal, contactSupport]
  );

  const [searchParams] = useSearchParams();

  useEffect(() => {
    // The `?cancel` deeplink is the shared legacy entry for BOTH cancel
    // and CFAR (the entry redirect maps the two actions to the same
    // URL), so it opens the legacy self-serve cancel iff at least one of
    // those actions is in-app actionable. A stale or direct URL for an
    // order whose cancel AND cfar the BFF resolved to commerce, or
    // answered unavailable/unresolved, must not enter the legacy flow.
    // No capabilities (shared tenants) passes. Gating on the cancel
    // route alone would break a legacy-CFAR entry if the two actions
    // ever diverged; the URL alone cannot say which action was meant.
    if (
      searchParams.has("cancel") &&
      (allowsInAppFlightAction(cancelRoute) ||
        allowsInAppFlightAction(cfarRoute))
    ) {
      onOpenModal(MyTripsModalTypes.SelfServeCancelFlight);
    }
    if (searchParams.has("support")) {
      onOpenModal(MyTripsModalTypes.ContactSupport);
    }
  }, [searchParams, onOpenModal, cancelRoute, cfarRoute]);

  const mapTitleKeys = ({
    titleKeys,
    ...summary
  }: FlightSummaryInfoWithKeys): FlightSummaryInfo => {
    const title = t("originToDestination", titleKeys);
    return {
      title,
      ...summary,
    };
  };

  // TODO: time validation should be done on BE
  const isWithin24HoursUntilDeparture = (
    flight: BookedFlightItineraryWithDepartureTime
  ) => {
    const timeDiff = dayjs(flight.departureTime).diff(dayjs(), "h");
    return timeDiff < 24;
  };

  const enableDisruptionRebookingContactSupport =
    useEnableDisruptionRebookingContactSupport();
  const maybeDisruptionAction = useRebookFlightTripAction(flight);

  const actions = useMemo(() => {
    const actions: IActionLink[] = [];

    if (!isPastTrips && (isWithin24HoursUntilDeparture(flight) || isNubank)) {
      actions.unshift({
        content: t("tripReviewLinks.checkInForFlight"),
        onClick: () => onOpenModal(MyTripsModalTypes.CheckInFlight),
        key: ActionLinkKey.CHECK_IN,
      });
    }

    if (!isCancelled && !isPastTrips) {
      // Commerce-destined CFAR. This is the new CTA for HTS orders, which
      // carry no legacy CFAR contract and so match none of the in-app
      // branches below.
      if (cfarRoute.kind === "commerce" && onCommerceFlightAction) {
        const { identifier } = cfarRoute;
        actions.push({
          linkClassName: "mobile-cfar-link",
          content: <>{t("cancelForAnyReason")}</>,
          onClick: () => onCommerceFlightAction(identifier, "cfar"),
          key: ActionLinkKey.CFAR,
        });
      }

      if (cancelRoute.kind === "commerce" && onCommerceFlightAction) {
        const { identifier } = cancelRoute;
        actions.push({
          content: t("tripReviewLinks.cancelFlight"),
          onClick: () => onCommerceFlightAction(identifier, "cancel"),
          key: ActionLinkKey.CANCELLATION,
        });
      }

      // The in-app chain is unchanged apart from the route guards, which
      // are constant `true` when the BFF sends no capabilities. A
      // commerce-destined action fails its guard here, so the two blocks
      // above can never double up with this one.
      if (showAirCfar && allowsInAppFlightAction(cfarRoute)) {
        const contractId = ancillaries?.cfar?.id?.policyId;
        const itineraryId = flight.bookedItinerary.id;
        actions.push({
          linkClassName: "mobile-cfar-link",
          content: <>{t("cancelForAnyReason")}</>,
          onClick: () => {
            dispatch(
              fetchFlightCfarCancellationQuote({
                contractId,
                itineraryId,
              })
            );
            dispatch(populateTripQueryParams(history));
          },
          key: ActionLinkKey.CFAR,
        });
      } else if (hasCfarCore && allowsInAppFlightAction(cfarRoute)) {
        actions.push({
          linkClassName: "mobile-cfar-link",
          content: <>{t("cancelForAnyReason")}</>,
          onClick: () =>
            navigate(
              `${flightsItineraryRelativeRoute(itineraryId)}/${cfarFlightsExerciseRelativeRoute()}`
            ),
          key: ActionLinkKey.CANCELLATION,
        });
      } else if (allowsInAppFlightAction(cancelRoute)) {
        actions.push({
          content: t("tripReviewLinks.cancelFlight"),
          onClick: () => onOpenModal(MyTripsModalTypes.SelfServeCancelFlight),
          key: ActionLinkKey.CANCELLATION,
        });
      }

      if (showAirChfar) {
        actions.push({
          content: <>{t("changeForAnyReason")}</>,
          onClick: () => {
            dispatch(setChfarExerciseProgress(ChfarExerciseProgress.Started));
            dispatch(populateTripQueryParams(history));
          },
          key: ActionLinkKey.CHFAR,
        });
      }

      if (enableAirExchange && !showAirChfar) {
        actions.push({
          content: t("tripReviewLinks.changeFlight"),
          onClick: () => onOpenModal(MyTripsModalTypes.ExchangeFlight),
          key: ActionLinkKey.EXCHANGE,
        });
      }
    }

    if (
      enableDisruptionRebookingContactSupport &&
      isMissedConnectionProtectionExercisable
    ) {
      actions.push({
        content: t("tripReviewLinks.missedConnectionExercise"),
        onClick: contactSupport,
        key: ActionLinkKey.MISSED_CONNECTION,
      });
    } else if (maybeDisruptionAction) {
      actions.push({
        content: maybeDisruptionAction.label,
        onClick: maybeDisruptionAction.action,
        key: ActionLinkKey.MISSED_CONNECTION,
      });
    }

    if (
      flight.rawStatus === Status.Ticketed ||
      flight.rawStatus === Status.Canceled
    ) {
      actions.push({
        content: t("tripReviewLinks.resendEmail"),
        onClick: () => onOpenModal(MyTripsModalTypes.ResendConfirmation),
        key: ActionLinkKey.RESEND_CONFIRMATION,
      });
    }

    actions.push({
      content: t("tripReviewLinks.getHelp"),
      onClick: () => onOpenModal(MyTripsModalTypes.ContactSupport),
      key: ActionLinkKey.HELP,
    });

    return actions;
  }, [
    ancillaries?.cfar?.id?.policyId,
    cancelRoute,
    cfarRoute,
    dispatch,
    enableAirExchange,
    flight,
    hasCfarCore,
    history,
    isCancelled,
    isPastTrips,
    itineraryId,
    maybeDisruptionAction,
    navigate,
    onCommerceFlightAction,
    onOpenModal,
    showAirCfar,
    showAirChfar,
    t,
    enableDisruptionRebookingContactSupport,
    contactSupport,
    isMissedConnectionProtectionExercisable,
  ]);

  const hasMajorScheduleChange =
    flight.bookedItinerary.scheduleChange?.severity ===
    ScheduleChangeSeverity.Major;

  const hasMinorScheduleChange =
    flight.bookedItinerary.scheduleChange?.severity ===
      ScheduleChangeSeverity.Minor ||
    (flight.status === Status.Modified &&
      !flight.bookedItinerary.scheduleChange);

  const flightInfoDetails = getFlightInfoDetails(
    flight,
    (date: string) =>
      dayjs(date).format(brand?.calendarDateLabelFormat || DATE_FORMAT),
    airportMap,
    airlineMap
  );

  const title = t("originToDestination", {
    origin: flightInfoDetails.origin,
    destination: flightInfoDetails.destination,
    interpolation: { escapeValue: false },
  });

  const titleTag = useMemo((): TagInfo | undefined => {
    switch (flight?.rawStatus) {
      case Status.Ticketed:
        return {
          label: t("confirmed"),
          type: TagColors.GREEN,
        };
      case Status.Booked:
      case Status.Pending:
        return {
          label: t("pending"),
          type: TagColors.GREY,
          iconName: IconName.InfoCircle,
          tooltipCopy: t("tooltip.pending") || "",
        };
      case Status.Canceled:
        return {
          label: t("cancelled"),
          type: TagColors.RED,
          iconName: hasMajorScheduleChange ? IconName.InfoCircle : undefined,
          tooltipCopy: hasMajorScheduleChange
            ? t("tooltip.cancelled") || ""
            : "",
        };
      case Status.Modified:
        return {
          label: t("itineraryModified"),
          type: hasMajorScheduleChange ? TagColors.RED : TagColors.YELLOW,
          iconName: hasMajorScheduleChange ? IconName.InfoCircle : undefined,
          tooltipCopy: hasMajorScheduleChange
            ? t("tooltip.modified") || ""
            : "",
        };
      case Status.Problem:
        return {
          label: t("problem"),
          type: TagColors.RED,
          iconName: IconName.InfoCircle,
          tooltipCopy: t("tooltip.problem"),
        };
      default:
        return undefined;
    }
  }, [flight?.rawStatus, hasMajorScheduleChange, t]);

  const passengers = useMemo(() => {
    const alone = flight.bookedItinerary.passengers.alone.map((ap) => {
      return ap;
    });
    const withLapInfants: AssociatedPassenger[] =
      flight.bookedItinerary.passengers.withLapInfants.reduce(
        (acc, passenger) => {
          const { adult, infant } = passenger;
          acc.push(adult, infant);
          return acc;
        },
        []
      );
    const allPassengers = [...alone, ...withLapInfants];

    const sortedPassengers = allPassengers.sort((a, b) => {
      const isAdultA = a.type === "ADT";
      const isAdultB = b.type === "ADT";
      if (isAdultA && !isAdultB) {
        return -1;
      }
      if (!isAdultA && isAdultB) {
        return 1;
      }
      return 0;
    });

    return sortedPassengers.map((p) => p.person);
  }, [flight]);

  const handleDisruptionProtectionCta = useCallback(() => {
    // A commerce-destined DAFAR capability replaces the in-app disruption
    // exercise route entirely; the commerce portal owns the eligibility
    // screen, so no local exercise progress is seeded.
    if (dafarRoute.kind === "commerce" && onCommerceFlightAction) {
      onCommerceFlightAction(dafarRoute.identifier, "dafar");
      return;
    }
    if (isNubank) {
      const shouldProceedToExerciseFlow =
        isDisruptionProtectionExercisable || isEligibleToBookReturn;
      if (shouldProceedToExerciseFlow) {
        setSelectedFlight(flight);
        dispatch(
          setDisruptionExerciseProgress(DisruptionExerciseProgress.LandingPage)
        );
      } else {
        setSelectedFlight(flight);
        dispatch(
          setDisruptionExerciseProgress(DisruptionExerciseProgress.NotEligible)
        );
      }
      const DISRUPTION_EXERCISE_PATH = "/trips/disruption/exercise";
      navigate(`${DISRUPTION_EXERCISE_PATH}/${flight.bookedItinerary?.id}`);
    } else {
      const shouldProceedToExerciseFlow =
        isDisruptionProtectionExercisable || isEligibleToBookReturn;

      if (shouldProceedToExerciseFlow) {
        setSelectedFlight(flight);
        dispatch(
          setDisruptionExerciseProgress(DisruptionExerciseProgress.LandingPage)
        );
      } else {
        setSelectedFlight(flight);
        dispatch(
          setDisruptionExerciseProgress(DisruptionExerciseProgress.NotEligible)
        );
      }
      navigate(`${PATH_DISRUPTION_EXERCISE}`);
    }
  }, [
    dafarRoute,
    dispatch,
    setSelectedFlight,
    isDisruptionProtectionExercisable,
    isEligibleToBookReturn,
    flight,
    onCommerceFlightAction,
    onOpenModal,
    isNubank,
    navigate,
  ]);

  // The DISRUPTED banner is this screen's DAFAR exercise entry, so
  // capabilities decide whether it is offered. A commerce-destined DAFAR
  // shows it for HTS orders, which carry no legacy contract and therefore
  // never satisfy `isDisruptionProtectionExercisable`; an Unavailable or
  // Unresolved DAFAR hides it even when the legacy contract says otherwise.
  const showDisruptionExercise =
    (dafarRoute.kind === "commerce" && !!onCommerceFlightAction) ||
    (allowsInAppFlightAction(dafarRoute) && isDisruptionProtectionExercisable);

  // The book-return banner enters the same disruption exercise flow, so it
  // follows the same capability gate: an Unavailable or Unresolved DAFAR
  // hides it. Tenants whose BFF sends no capabilities pass the gate and
  // keep today's behavior.
  const disruptionBanner = showDisruptionExercise ? (
    <FlightDisruptionAlertBanner
      onClick={handleDisruptionProtectionCta}
      type={DisruptionAlertBannerType.DISRUPTED}
      productName={t("core-disruption.productName")}
    ></FlightDisruptionAlertBanner>
  ) : (
    allowsInAppFlightAction(dafarRoute) &&
    !isDisruptionProtectionExercisable &&
    isEligibleToBookReturn && (
      <FlightDisruptionAlertBanner
        onClick={handleDisruptionProtectionCta}
        type={DisruptionAlertBannerType.BOOK_RETURN}
        productName={t("core-disruption.productName")}
        destination={flightInfoDetails.destination}
      ></FlightDisruptionAlertBanner>
    )
  );

  // PDA will not be available for purchase on VI flights
  const disruptionEntry = hasDisruptionProtection && !isVirtualInterlining && (
    <div className="disruption-entry-container">
      <FlightDisruptionAlertBanner
        onClick={handleDisruptionProtectionCta}
        type={DisruptionAlertBannerType.ENTRY_POINT}
        productName={t("core-disruption.productName")}
      ></FlightDisruptionAlertBanner>
    </div>
  );

  if (!flight) {
    return null;
  }
  return (
    <div
      className={clsx(
        "mobile-trip-details-uber",
        "mobile-trip-details",
        getEnvVariables("clientName")
      )}
    >
      <div className="mobile-trip-details-header">
        <ButtonWrap
          className="mobile-trip-details-header-button"
          onClick={() => goBack()}
        >
          {clientContext?.assets?.back ? (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img src={clientContext.assets.back} className="back-arrow" />
          ) : (
            <IconComponent
              ariaLabel="back icon"
              className="back-arrow"
              name={isNubank ? IconName.Chevron : IconName.ArrowRight}
            />
          )}
        </ButtonWrap>
        <div className="mobile-trip-details-header-title">
          {t("tripSummary")}
        </div>
      </div>
      <StatusBanner
        flight={flight}
        hasMajorScheduleChange={hasMajorScheduleChange}
        hasMinorScheduleChange={hasMinorScheduleChange}
        isMobile={true}
        setOpenModal={() => onOpenModal(MyTripsModalTypes.ScheduleChange)}
      />

      <div className="mobile-trip-details-info-container">
        {clientContext?.assets?.tripDetails ? (
          <div className="mobile-trip-details-icon-container">
            <img
              alt=""
              src={clientContext.assets.tripDetails}
              className="mobile-trip-details-icon"
            />
          </div>
        ) : null}
        {/* Top section */}
        <Slot
          id="trip-details-summary"
          flightInfoDetails={flightInfoDetails}
          onClick={() => onOpenModal(MyTripsModalTypes.ConfirmationModal)}
          confirmationNumbers={confirmationNumbers}
          title={title}
          titleTag={titleTag}
          isMultiAirline={isMultiTypeTicket}
          flight={flight}
          airlineMap={airlineMap}
          disruptionBanner={disruptionBanner}
          component={
            <div className="mobile-trip-details-summary">
              <div className="mobile-trip-details-locations">{title}</div>
              <div className="mobile-trip-details-dates">
                {`${flightInfoDetails?.startDate} ${
                  flightInfoDetails?.endDate
                    ? ` - ${flightInfoDetails?.endDate}`
                    : ""
                }`}
              </div>
              <ButtonWrap
                className="mobile-trip-details-flight-details"
                onClick={() => onOpenModal(MyTripsModalTypes.ConfirmationModal)}
              >
                {titleTag ? (
                  <StatusTag
                    className="mobile-trip-details-status"
                    tagInfo={titleTag!}
                  />
                ) : null}
                {disruptionBanner}
                <h3 className="mobile-trip-details-reservation-numbers-title">
                  {t("reservationNumbers")}
                </h3>

                <Divider className="mobile-trip-details-divider-reservation-numbers" />
                <div className="mobile-trip-details-confirmation-numbers-container">
                  {confirmationNumbers
                    ? confirmationNumbers.map((confirmationNumber, i) => {
                        // TODO: Update to be a filter instead of relying on the index
                        // the first confirmationNumber is the locator for the tenant - Uber wants this shown last
                        return i !== 0 ? (
                          <div
                            className="mobile-trip-details-confirmation-number"
                            key={`flight-confirmation-numbers-${i}`}
                          >
                            <p className="flight-confirmation-number">
                              {confirmationNumber.locator}
                            </p>
                            <p className="flight-confirmation-label">
                              {confirmationNumber.label}
                            </p>
                          </div>
                        ) : null;
                      })
                    : flightInfoDetails?.flightNumber}
                  <div className="mobile-trip-details-confirmation-number">
                    <p className="flight-confirmation-number">
                      {flightInfoDetails?.confirmationCode}
                    </p>
                    <p className="flight-confirmation-label">{t("uber")}</p>
                  </div>
                </div>
              </ButtonWrap>
            </div>
          }
        />
        <Divider className="mobile-trip-details-divider" />

        {/* Middle Section */}
        {isMultiTypeTicket ? (
          <div className="mobile-trip-details-multi-airline">
            {t("multiAirlineTrip")}
          </div>
        ) : null}
        <div className="mobile-trip-details-info">
          <div className="mobile-trip-details-flight-summaries">
            <MobileFlightSummaryRowNew
              flightSummaryInfo={mapTitleKeys(
                getFlightSummaryInfo(true, flight, airportMap, airlineMap)
              )}
              onClick={() => onOpenModal(MyTripsModalTypes.OutboundItinerary)}
              iconSrc={clientContext.assets?.airplaneDepart}
              customIcon={
                isNubank ? (
                  <AirlineIcon
                    airlineCode={
                      mapTitleKeys(
                        getFlightSummaryInfo(
                          true,
                          flight,
                          airportMap,
                          airlineMap
                        )
                      )?.airlineCode
                    }
                  />
                ) : null
              }
              customExpandElement={
                isNubank ? (
                  <img
                    alt="Expand icon"
                    className="mobile-right-chevron"
                    src={clientContext.assets?.chevronDown}
                  />
                ) : undefined
              }
            />
            {getReturnSlice(flight.bookedItinerary) ? (
              <MobileFlightSummaryRowNew
                flightSummaryInfo={mapTitleKeys(
                  getFlightSummaryInfo(false, flight, airportMap, airlineMap)
                )}
                onClick={() => onOpenModal(MyTripsModalTypes.ReturnItinerary)}
                iconSrc={clientContext.assets?.airplaneArrive}
                customIcon={
                  isNubank ? (
                    <AirlineIcon
                      airlineCode={
                        mapTitleKeys(
                          getFlightSummaryInfo(
                            false,
                            flight,
                            airportMap,
                            airlineMap
                          )
                        )?.airlineCode
                      }
                    />
                  ) : null
                }
                customExpandElement={
                  isNubank ? (
                    <img
                      alt="Expand icon"
                      className="mobile-right-chevron"
                      src={clientContext.assets?.chevronDown}
                    />
                  ) : undefined
                }
              />
            ) : null}
          </div>
          {!isNubank ? (
            <Slot
              id="trip-flights-details-insurance"
              flight={flight}
              airportMap={airportMap}
              isRoundTrip={
                getReturnSlice(flight.bookedItinerary) ? true : false
              }
            />
          ) : null}
          <Divider className="mobile-trip-details-divider" />
          <div
            className={clsx("mobile-trip-details-passenger-info-container", {
              "no-padding": !flight.bookedItinerary?.paymentBreakdown,
            })}
          >
            <PassengerSummaryRow
              passengers={passengers as IPerson[]}
              iconSrc={clientContext?.assets?.passenger}
              review
            />
          </div>
          <Slot
            id="mytrips-flights-details-baggage"
            itineraryBaggage={flight?.bookedItinerary?.baggage}
            passengers={passengers}
            restrictions={flight?.bookedItinerary?.restrictions}
            airlineHomePage={
              airlineMap[flightInfoDetails.airlineCode]?.webLinks?.homePage
            }
          />
          <Slot
            id="mytrips-flights-details-seats"
            itinerarySeats={flight?.bookedItinerary?.seats}
            passengers={passengers}
            slices={flight?.bookedItinerary?.travelItinerary?.slices ?? []}
            lapInfantIds={flight.bookedItinerary.passengers.withLapInfants.map(
              (p) => p.infant.person.id
            )}
            airlineHomePage={
              airlineMap[flightInfoDetails.airlineCode]?.webLinks?.homePage
            }
          />
          {flight.bookedItinerary?.paymentBreakdown ||
          flight.bookedItinerary?.paymentAmountInfo ? (
            <>
              <Divider className="mobile-trip-details-divider" />
              <div className="mobile-trip-details-total-price">
                <UberPriceSummaryRow
                  priceString={formatFiatCurrency(
                    getFlightTotalWithSeats(flight),
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                  iconSrc={clientContext?.assets?.card}
                  onClick={() => onOpenModal(MyTripsModalTypes.PaymentModal)}
                  roundTrip={
                    getReturnSlice(flight.bookedItinerary) ? true : false
                  }
                  customExpandIcon={
                    isNubank ? (
                      <img src={clientContext?.assets?.chevron} alt="" />
                    ) : null
                  }
                />
              </div>
            </>
          ) : null}
          {isNubank ? (
            <Slot
              id="trip-flights-details-insurance"
              flight={flight}
              airportMap={airportMap}
              isRoundTrip={
                getReturnSlice(flight.bookedItinerary) ? true : false
              }
            />
          ) : null}
        </div>

        {/* Bottom section */}
        <div className="mobile-trip-details-manage">
          <div className="mobile-trip-details-manage-title">
            {t("manageMyTrip")}
          </div>
          <Slot
            id="trip-details-links"
            actions={actions}
            component={<MobileActionLinks actions={actions} />}
          />
        </div>

        <UberExchangeFlightModalContent
          flight={flight}
          onClose={goBack}
          open={selfServePage === ISelfServePage.exchange}
          openSelfServeCancel={() => setSelfServePage(ISelfServePage.cancel)}
          confirmationNumbers={confirmationNumbers}
        />
      </div>

      {status === Status.Canceled && hasFTC && (
        <Popover
          anchorEl={ftcPopoverRef.current}
          anchorOrigin={{
            horizontal: "left",
            vertical: "bottom",
          }}
          className="how-ftc-works-popover"
          onClose={closeFTCPopover}
          open={ftcPopoverOpen}
        >
          <Typography className="subtitle">{travelCreditCopy}</Typography>
        </Popover>
      )}
    </div>
  );
};
